import { useCallback, useEffect, useMemo, useState } from 'react'

/** Drohne im Einsatz bzw. im Katalog (Antwort von /einsaetze/{id}/drohnen und /drohnen/katalog) */
export interface EinsatzDrohne {
  drohne_id: number
  asb_inventarnummer?: string | null
  kennzeichen?: string | null
  bezeichnung?: string | null
  seriennummer?: string | null
  hersteller?: string | null
  modell?: string | null
  status?: string | null
  besitzer_label?: string | null
  freigegeben?: boolean
  zugeordnet?: boolean
  gebunden_in?: { einsatz_id: number; titel: string }[]
}

/** Minimale Schnittstelle des axios-Clients der Apps (Basis-URL /api/v1) */
export interface EinsatzDrohnenApi {
  get: (url: string) => Promise<{ data: any }>
  put: (url: string, body: any) => Promise<{ data: any }>
}

/** „INV-1 · Bezeichnung · Hersteller Modell“ */
export const drohnenKurz = (d: EinsatzDrohne) =>
  [d.asb_inventarnummer, d.bezeichnung || d.kennzeichen, [d.hersteller, d.modell].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || `Drohne #${d.drohne_id}`

/** Zugeordnete Drohnen des Einsatzes zuerst (stabil), Rest darunter. Liefert die Liste und die Zahl der zugeordneten. */
export function sortiereDrohnenNachEinsatz<T extends { id: number }>(liste: T[], zugeordnetIds: number[]): { liste: T[]; zugeordnet: Set<number> } {
  const z = new Set(zugeordnetIds)
  return { liste: [...liste.filter(d => z.has(d.id)), ...liste.filter(d => !z.has(d.id))], zugeordnet: z }
}

const kapsel = 'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide'

/**
 * „Drohnen im Einsatz“: Mehrfachauswahl aus dem Drohnen-Katalog (keine Pflicht).
 * bearbeitbar = false: nur die zugeordneten Drohnen anzeigen (auch freigegebene, als „freigegeben“).
 * Änderungen werden sofort gespeichert (PUT /einsaetze/{id}/drohnen); der Einsatz muss schon existieren.
 */
export function EinsatzDrohnen({ einsatzId, api, bearbeitbar, titel = 'Drohnen im Einsatz' }: {
  einsatzId: number | null | undefined
  api: EinsatzDrohnenApi
  bearbeitbar: boolean
  titel?: string
}) {
  const [zugeordnet, setZugeordnet] = useState<EinsatzDrohne[]>([])
  const [katalog, setKatalog] = useState<EinsatzDrohne[]>([])
  const [suche, setSuche] = useState('')
  const [laedt, setLaedt] = useState(false)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState('')

  const laden = useCallback(async () => {
    if (!einsatzId) return
    setLaedt(true); setFehler('')
    try {
      const z = await api.get(`/einsaetze/${einsatzId}/drohnen`)
      setZugeordnet(Array.isArray(z.data) ? z.data : [])
      if (bearbeitbar) {
        const k = await api.get(`/einsaetze/${einsatzId}/drohnen/katalog`)
        setKatalog(Array.isArray(k.data) ? k.data : [])
      }
    } catch {
      setFehler('Drohnen konnten nicht geladen werden.')
    } finally {
      setLaedt(false)
    }
  }, [einsatzId, api, bearbeitbar])

  useEffect(() => { void laden() }, [laden])

  const ids = useMemo(() => new Set(zugeordnet.map(d => d.drohne_id)), [zugeordnet])
  const freigegeben = useMemo(() => new Map(zugeordnet.map(d => [d.drohne_id, !!d.freigegeben])), [zugeordnet])

  async function umschalten(id: number) {
    if (!einsatzId || speichert) return
    const neu = new Set(ids)
    if (neu.has(id)) neu.delete(id); else neu.add(id)
    setSpeichert(true); setFehler('')
    try {
      const r = await api.put(`/einsaetze/${einsatzId}/drohnen`, { drohne_ids: [...neu] })
      setZugeordnet(Array.isArray(r.data) ? r.data : [])
      const k = await api.get(`/einsaetze/${einsatzId}/drohnen/katalog`)
      setKatalog(Array.isArray(k.data) ? k.data : [])
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setFehler(typeof d === 'string' ? d : 'Zuordnung konnte nicht gespeichert werden.')
    } finally {
      setSpeichert(false)
    }
  }

  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase()
    const liste = q ? katalog.filter(d => [d.asb_inventarnummer, d.bezeichnung, d.kennzeichen, d.hersteller, d.modell, d.besitzer_label, d.seriennummer]
      .some(x => (x ?? '').toLowerCase().includes(q))) : katalog
    return [...liste.filter(d => ids.has(d.drohne_id)), ...liste.filter(d => !ids.has(d.drohne_id))]
  }, [katalog, suche, ids])

  return (
    <div className="pt-4 border-t border-dark-border" data-testid="einsatz-drohnen">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-dark-text">{titel}
        {zugeordnet.length > 0 && <span className="text-xs font-normal text-dark-text-subtle">({zugeordnet.length})</span>}
      </h3>
      {!einsatzId ? (
        <p className="mt-2 text-xs text-dark-text-subtle">Drohnen lassen sich zuordnen, sobald der Einsatz gespeichert ist.</p>
      ) : laedt && zugeordnet.length === 0 && katalog.length === 0 ? (
        <p className="mt-2 text-xs text-dark-text-muted">Lade Drohnen…</p>
      ) : !bearbeitbar ? (
        zugeordnet.length === 0 ? (
          <p className="mt-2 text-xs text-dark-text-subtle">Keine Drohnen zugeordnet (keine Pflicht). Beim Flugstart wird die verwendete Drohne automatisch zugeordnet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-dark-border overflow-hidden rounded-xl border border-dark-border">
            {zugeordnet.map(d => (
              <li key={d.drohne_id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm text-dark-text">{drohnenKurz(d)}</div>
                  <div className="text-[11px] text-dark-text-subtle">Besitzer: {d.besitzer_label || '—'}</div>
                </div>
                <span className={`${kapsel} ${d.freigegeben ? 'bg-dark-bg-tertiary text-dark-text-subtle' : 'bg-primary-600/20 text-primary-300'}`}>
                  {d.freigegeben ? 'freigegeben' : 'gebunden'}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="mt-3 space-y-2">
          <input
            value={suche} onChange={e => setSuche(e.target.value)} placeholder="Drohne suchen (Inventarnr., Bezeichnung, Modell, Besitzer)"
            className="w-full rounded-lg border border-dark-border bg-dark-bg-tertiary px-3 py-2 text-sm text-dark-text placeholder:text-dark-text-subtle"
          />
          <div className="max-h-72 overflow-y-auto rounded-xl border border-dark-border">
            {sichtbar.length === 0 ? (
              <p className="px-3 py-3 text-xs text-dark-text-subtle">Keine Drohnen gefunden.</p>
            ) : sichtbar.map(d => {
              const an = ids.has(d.drohne_id)
              return (
                <label key={d.drohne_id} className="flex cursor-pointer items-start gap-3 border-t border-dark-border px-3 py-2 first:border-t-0 hover:bg-dark-bg-tertiary">
                  <input type="checkbox" className="mt-1" checked={an} disabled={speichert} onChange={() => umschalten(d.drohne_id)} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-dark-text">{drohnenKurz(d)}</div>
                    <div className="text-[11px] text-dark-text-subtle">Besitzer: {d.besitzer_label || '—'}</div>
                    {(d.gebunden_in ?? []).length > 0 && (
                      <div className="mt-0.5 text-[11px] text-amber-400" data-testid="drohne-gebunden-hinweis">
                        Achtung: gebunden in Einsatz {(d.gebunden_in ?? []).map(g => g.titel || `#${g.einsatz_id}`).join(', ')}
                      </div>
                    )}
                  </div>
                  {an && freigegeben.get(d.drohne_id) && <span className={`${kapsel} bg-dark-bg-tertiary text-dark-text-subtle`}>freigegeben</span>}
                </label>
              )
            })}
          </div>
          <p className="text-xs text-dark-text-subtle">
            Keine Pflicht. Die Drohnen sind gebunden, bis der Einsatz bereit ist (Einsatzbereitschaft wiederhergestellt); danach werden sie freigegeben und bleiben für den Bericht gespeichert.
          </p>
        </div>
      )}
      {fehler && <p className="mt-2 text-xs text-red-300">{fehler}</p>}
    </div>
  )
}
